if (!customElements.get('configurable-product')) {
  customElements.define(
    'configurable-product',
    class ConfigurableProduct extends HTMLElement {
      constructor() {
        super();
        // reset scroll to prevent bug with inner scroll for product info section
        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'manual';
        }
        this.initialized = false;
        this.debug = false;
        this.initial_page_load = true;
        if (this.debug) console.log('ConfigurableProduct constructor');
        this.observer = null;
        this.model = false;
        this.variant_sku = false;
        this.variant_available = false;
        this.variant_inventory_lbc = false;
        this.hide_notify_me = false;
        this.published_for_personalization = false;
        this.handle = false;
        this.finish = false;
        this.quantity = false;
        this.customize = false;
        this.bundle = false;
        this.cart_title = false;
        this.variant_option_1_title = false;
        this.variant_option_2_title = false;
        this.add_ons = {}; // Initialize an empty object for add-ons
        this.swipers = {};

        // Clear any previously stored product data from localStorage
        window.localStorage.removeItem('product_id');
        window.localStorage.removeItem('variant_id');
        window.localStorage.removeItem('finish');
        window.localStorage.removeItem('previous_finish');

        // Initialize num_add_ons here, but attribute will be accessed in connectedCallback()
        this.num_add_ons = null;
        this.swipers = {};
      }

      /**
       * Helper method to find a specification value by title from an array of specs
       * Uses dynamic DOM-based matching to handle different languages
       *
       * @param {Array} specs - Array of specification objects with title and value properties
       * @param {string} selector - CSS selector for the spec element to match (e.g., '[spec-product-capacity]')
       * @returns {string} The found specification value or empty string
       */
      findSpecValue(specs, selector) {
        if (!Array.isArray(specs) || !selector) {
          return '';
        }

        // Find the spec value element in the DOM
        const specValueElement = document.querySelector(selector);
        if (!specValueElement) {
          if (this.debug) console.warn(`Spec value element not found for selector: ${selector}`);
          return '';
        }

        // Find the container that holds both title and value
        const specContainer = specValueElement.closest('.product-spec__copy-inner, [data-spec]');
        if (!specContainer) {
          if (this.debug) console.warn(`Spec container not found for selector: ${selector}`);
          return '';
        }

        // Get the title from the spec container (look for .spec__row-title or similar)
        const titleElement = specContainer.querySelector('.spec__row-title, .spec-title, [class*="title"]');
        if (!titleElement) {
          if (this.debug) console.warn(`Title element not found in spec element: ${selector}`);
          return '';
        }

        const actualTitle = titleElement.textContent?.trim();
        if (!actualTitle) {
          if (this.debug) console.warn(`No title text found in element: ${selector}`);
          return '';
        }

        // Find the spec in the JSON data that matches the actual title
        const spec = specs.find((spec) => {
          if (!spec?.title) return false;
          // Case-insensitive comparison, also handle variations with/without colons
          const specTitle = spec.title.toLowerCase().trim();
          const domTitle = actualTitle.toLowerCase().trim();

          return (
            specTitle === domTitle ||
            specTitle === domTitle + ':' ||
            specTitle + ':' === domTitle ||
            specTitle.replace(':', '') === domTitle.replace(':', '')
          );
        });

        if (spec && this.debug) {
          console.log(`Found spec match: "${actualTitle}" -> "${spec.value}"`);
        } else if (!spec && this.debug) {
          console.warn(`No spec found for title: "${actualTitle}"`);
        }

        return spec?.value || '';
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        if (this.debug) console.log('ConfigurableProduct connectedCallback');
        if (this.getAttribute('data-selected-variant-id')) {
          this.product_id = this.getAttribute('data-selected-product-id');
          this.variant_id = this.getAttribute('data-selected-variant-id');
          this.disable_pickers = true;
        }
        this.type = this.getAttribute('data-product-type');
        // Access data-num-add-ons attribute and dynamically create add-ons structure
        this.num_add_ons = parseInt(this.getAttribute('data-num-add-ons'));
        // Dynamically create the add-ons structure
        for (let i = 1; i <= this.num_add_ons; i++) {
          this.add_ons[`add_on_${i}`] = {
            product_id: false,
            variant_id: false,
          };
        }
        this.setupObserver();
        document.addEventListener('DOMContentLoaded', () => {
          this.setupSwiperSliders(); // Call the method on DOM ready
          this.form = this.querySelector('.add-to-cart-form');
          this.drop_down_lists = this.querySelectorAll('.AddOnDropdown');
          const selected_product_json = document.querySelector(
            `script[data-json-config-product-id="${this.product_id}"]`
          );
          this.setConfiguarableProductValues(selected_product_json);
          this.setupFinishPicker();
          const config_model = this.querySelector('config-model');
          if (this.getAttribute('data-selected-variant-id') || this.model == false || !config_model) {
            this.prePickConfigurableSelections();
          }
          this.updateAddOnItems();
          //          this.desktopScroll();
          this.showSpecsModalOpener();
          // Only enable sticky mobile swiper when vertical viewport is at least 1.5x horizontal
          if (window.innerHeight >= window.innerWidth * 1.5) {
            this.handleStickyMobileSwiper();
          }
          this.updateSelectedFinishMeta();
          this.setProgressBar();

          window.addEventListener('load', () => {
            this.matchAddOnFinish();
            this.loadLazyImagesWithFallback();
          });
        });
      }

      disconnectedCallback() {}

      setConfiguarableProductValues(selected_product_variant_json, selector = 'variant', selector_value = false) {
        if (this.debug) console.log('setConfiguarableProductValues');
        if (this.debug) console.log('selected_product_variant_json: ', selected_product_variant_json);
        if (this.debug) console.log('selector: ', selector);
        if (this.debug) console.log('selector_value: ', selector_value);
        let targetVariant;
        const jsonData = JSON.parse(selected_product_variant_json.innerHTML);
        if (selector == 'finish') {
          // if finish is provided, we are trying to look up this variant by finish instead of by variant_id
          targetVariant = jsonData.variants.find(function (variant) {
            return variant.finish === selector_value;
          });
        } else if (this.variant_id) {
          // if finish is not provided, but variant_id exists, we are trying to look up this variant by variant_id
          const this_variant_id = this.variant_id;
          targetVariant = jsonData.variants.find(function (variant) {
            return variant.id === this_variant_id;
          });
        } else {
          // if finish and variant_id are not provided, then return the id of the first variant
          targetVariant = jsonData.variants[0];
        }

        if (selected_product_variant_json) {
          this.product_id = selected_product_variant_json.getAttribute('data-json-config-product-id');
          this.model = selected_product_variant_json.getAttribute('data-json-config-model').toUpperCase();
          this.quantity = selected_product_variant_json.getAttribute('data-json-config-pack-size');
          this.customize = selected_product_variant_json.getAttribute('data-json-config-is-customize-option');
          this.bundle = selected_product_variant_json.getAttribute('data-json-config-is-bundle-option');
          this.cart_title = selected_product_variant_json.getAttribute('data-json-config-cart-title');
        }

        if (targetVariant) {
          this.variant_id = targetVariant.id;
          this.finish = targetVariant.finish;
          this.handle = targetVariant.handle;
          this.variant_available = targetVariant.available;
          this.variant_sku = targetVariant.sku;
          this.hide_notify_me = targetVariant.hide_notify_me;
          this.published_for_personalization = targetVariant.published_for_personalization;
          if (targetVariant.inventory !== undefined && targetVariant.inventory !== null) {
            this.variant_inventory_lbc = Number(targetVariant.inventory['Long Beach Warehouse']) || 0;
          } else {
            this.variant_inventory_lbc = 0;
          }
          this.updateCustomizationVisibility();
        } else {
          this.setupFinishPicker();
          return;
        }

        const addOns = jsonData.add_ons;

        console.log('addOns', addOns);

        // store product_id and variant_id in local storage
        window.localStorage.setItem('product_id', this.product_id);
        window.localStorage.setItem('variant_id', this.variant_id);
        window.localStorage.setItem('finish', this.finish);
        // reset all addon_#_variant_id
        for (let i = 1; i <= this.num_add_ons; i++) {
          this[`add_on_${i}_product_id`] = false;
        }
        if (addOns.length > 0) {
          addOns.forEach((addOn, index) => {
            this[`add_on_${index + 1}_product_id`] = addOn.id;
          });

          // loop through all data-config-add-on-index elements and set the title to the value of the option-title
          const config_add_ons = this.querySelectorAll('[data-config-add-on-index][data-button-state="inactive"]');
          config_add_ons.forEach((config_add_on) => {
            const add_on_default_price_element = config_add_on.querySelector('[data-variant-price]');
            if (add_on_default_price_element) {
              const add_on_default_price = add_on_default_price_element.getAttribute('data-variant-price');
              const data_variant_option2 = add_on_default_price_element.getAttribute('data-variant-option2');
              const option_text = data_variant_option2 + ' | ' + add_on_default_price;
              // find closest .DropdownSelector and set the textContent to the textContent of the item clicked
              const drop_down_selector = config_add_on.querySelector('.DropdownSelector .Edit');
              drop_down_selector.textContent = option_text;
            }
          });
        }
        if (this.debug) console.log('setConfiguarableProductValues');
        if (this.debug) console.log('this.model: ', this.model);
        if (this.debug) console.log('this.product_id: ', this.product_id);
        if (this.debug) console.log('this.variant_id: ', this.variant_id);
        if (this.debug) console.log('this.variant_available: ', this.variant_available);
        if (this.debug) console.log('this.variant_sku: ', this.variant_sku);
        if (this.debug) console.log('this.variant_inventory_lbc: ', this.variant_inventory_lbc);
        if (this.debug) console.log('this.finish: ', this.finish);
        if (this.debug) console.log('this.quantity: ', this.quantity);
        if (this.debug) console.log('this.customize: ', this.customize);
        if (this.debug) console.log('this.bundle: ', this.bundle);

        //        this.updateURL();
      }

      updateCustomizationVisibility() {
        if (this.published_for_personalization == 'false' && this.variant_sku && !this.variant_sku.includes('+PR')) {
          const noPersonalizationBtn = document.querySelector('[data-config-customize="false"]');
          if (noPersonalizationBtn) {
            noPersonalizationBtn.click();
          }
          // Hide config-customize element when Long Beach inventory is 0
          const configCustomize = document.querySelector('config-customize');
          if (configCustomize) {
            configCustomize.style.height = '0';
            configCustomize.style.overflow = 'hidden';
            configCustomize.style.opacity = '0';
          }
        }
        // If Long Beach inventory is 0, select "no personalization" option
        else if (this.variant_inventory_lbc < 5) {
          const noPersonalizationBtn = document.querySelector('[data-config-customize="false"]');
          if (noPersonalizationBtn) {
            noPersonalizationBtn.click();
          }
          // Hide config-customize element when Long Beach inventory is 0
          const configCustomize = document.querySelector('config-customize');
          if (configCustomize) {
            configCustomize.style.height = '0';
            configCustomize.style.overflow = 'hidden';
            configCustomize.style.opacity = '0';
          }
        } else {
          // Show config-customize element when Long Beach inventory is not 0
          const configCustomize = document.querySelector('config-customize');
          if (configCustomize) {
            configCustomize.style.height = 'auto';
            configCustomize.style.overflow = 'visible';
            configCustomize.style.opacity = '1';
          }
        }
      }

      updateURL() {
        if (this.debug) console.log('updateURL');
        // check if this is the initial page load, if it is, then don't update the URL for utm tracking
        if (this.initial_page_load) {
          this.initial_page_load = false;
          return;
        }
        let this_handle = this.handle;
        let this_variant_parameter = '';
        if (this.variant_id) {
          this_variant_parameter = `?variant=${this.variant_id}`;
        }
        // if this_handle has 'with-personalization' in it, remove it
        if (this_handle && this_handle.includes('with-personalization')) {
          this_handle = this_handle.replace('-with-personalization', '');
          this_variant_parameter = '';
        }
        const routes = Shopify.routes;
        window.history.replaceState(
          { ...window.history.state },
          '',
          `${routes.root}products/${this_handle}${this_variant_parameter}`
        );
        // setting previousUrl in sessionStorage due to making sure simple-pdp doesn't conflict
        const currentUrl = window.location.href;
        sessionStorage.setItem('previousUrl', currentUrl);
      }

      prePickConfigurableSelections() {
        // pre-pick the selections based on the data attributes of the element
        if (this.debug) console.log('prePickConfigurableSelections');
        const config_model = this.querySelector('config-model');
        const config_finish = this.querySelector('config-finish');
        if (this.model && config_model) {
          if (this.debug) console.log('try to click this model: ', this.model);
          const config_model_buttons = config_model.querySelectorAll('[data-config-model]');
          config_model_buttons.forEach((config_button) => {
            if (config_button.getAttribute('data-config-model') === this.model) {
              config_button.click();
            }
          });
          if (this.finish && config_finish) {
            if (this.debug) console.log('try to click this finish: ', this.finish);
            const config_finish_button = this.querySelector(
              '[data-config-model="' + this.model + '"][data-config-finish="' + this.finish + '"]'
            );
            config_finish_button.click();
          } else if (this.variant_id && config_finish) {
            if (this.debug) console.log('try to click this variant_id: ', this.variant_id);
            const config_finish_buttons = config_finish.querySelectorAll(
              '[data-config-finish][data-config-variant-id="' + this.variant_id + '"]'
            );
            config_finish_buttons.forEach((config_button) => {
              if (config_button.getAttribute('data-config-variant-id') === this.variant_id) {
                config_button.click();
              }
            });
          }
        } else if (this.variant_id) {
          if (this.debug) console.log('try to click this variant_id: ', this.variant_id);
          const config_finish_buttons = config_finish.querySelectorAll(
            '[data-config-finish][data-config-variant-id="' + this.variant_id + '"]'
          );
          config_finish_buttons.forEach((config_button) => {
            if (config_button.getAttribute('data-config-variant-id') === this.variant_id) {
              config_button.click();
            }
          });
        } else if (this.finish) {
          if (this.debug) console.log('try to click this finish: ', this.finish);
          const config_finish_buttons = config_finish.querySelectorAll(
            '[data-config-finish][data-config-model="' + this.model + '"]'
          );
          config_finish_buttons.forEach((config_button) => {
            if (config_button.getAttribute('data-config-finish') === this.finish) {
              config_button.click();
            }
          });
        }
        if (this.quantity) {
          const config_quantity = this.querySelector('config-quantity');
          if (config_quantity) {
            const config_quantity_buttons = config_quantity.querySelectorAll('[data-config-quantity]');
            config_quantity_buttons.forEach((config_button) => {
              if (config_button.getAttribute('data-config-quantity') === this.quantity) {
                config_button.click();
              }
            });
          }
        }
        this.disable_pickers = false;
      }

      // Function to set up the MutationObserver
      setupObserver() {
        if (this.debug) console.log('setupObserver()');

        const configurable_product_container = document.querySelector('configurable-product');
        configurable_product_container.attachConfigListeners();
      }

      attachConfigListeners() {
        if (this.debug) console.log('attachConfigListeners');
        this.querySelectorAll('[data-section-state]').forEach((config_section) => {
          this.attachConfigSectionListener(config_section);
        });

        const drop_down_selectors = this.querySelectorAll('.DropdownSelector:not(.no-open)');
        drop_down_selectors.forEach((drop_down_selector) => {
          if (this.debug) console.log('drop_down_selector: ', drop_down_selector);
          this.attachDropdownSelectorListener(drop_down_selector);
        });
        const drop_down_items = this.querySelectorAll('.DropdownItem');
        drop_down_items.forEach((drop_down_item) => {
          if (this.debug) console.log('drop_down_item: ', drop_down_item);
          this.attachDropdownItemListener(drop_down_item);
        });

        // Function to close menu when clicking outside
        document.addEventListener('click', (event) => {
          if (!event.target.closest('.DropdownList') && !event.target.closest('.DropdownSelector')) {
            this.drop_down_lists.forEach((drop_down_list) => drop_down_list.classList.remove('opened'));
          }
        });
      }
      attachDropdownSelectorListener(drop_down_selector) {
        console.log('attachDropdownSelectorListener', drop_down_selector);
        // add click listener
        drop_down_selector.addEventListener('click', (event) => {
          event.preventDefault();
          // find closest config-add-on and if data-section-state is not "active" then do nothing
          const config_add_on = drop_down_selector.closest('config-add-on');
          if (config_add_on.getAttribute('data-section-state') !== 'active') {
            return;
          }

          const dropdown = drop_down_selector.closest('.AddOnDropdown');
          dropdown?.classList.toggle('opened');
          if (!dropdown?.hasAttribute('data-is-item-selected')) {
            dropdown?.setAttribute('data-button-state', 'initial');
          }
        });
      }
      attachDropdownItemListener(drop_down_item) {
        if (this.debug) console.log('attachDropdownItemListener');
        // add click listener
        drop_down_item.addEventListener('click', (event) => {
          event.preventDefault();

          // find closest config-add-on and if data-section-state is not "active" then do nothing
          const config_add_on = drop_down_item.closest('config-add-on');
          if (config_add_on.getAttribute('data-section-state') !== 'active') {
            return;
          }
          // find closest .DropdownList and display none to close it
          const dropdown = drop_down_item.closest('.AddOnDropdown');
          dropdown?.classList.remove('opened');
          dropdown?.setAttribute('data-is-item-selected', '');
          dropdown?.setAttribute('data-button-state', 'active');

          // find closest .DropdownSelector and set the textContent to the textContent of the item clicked
          const dropdownSelectorTitle = dropdown.querySelector('.DropdownSelector .option-button-text');
          const dropdownItemTitle = drop_down_item.querySelector('.option-button-text');
          const dropdownSelectorDetails = dropdown.querySelector('.DropdownSelector .option-btn-sub-text');
          const dropdownItemDetails = drop_down_item.querySelector('.option-btn-sub-text');

          const dropdownItemVariantId = drop_down_item.getAttribute('data-config-add-on-variant-id');

          dropdownSelectorTitle.textContent = dropdownItemTitle?.textContent;
          dropdownSelectorDetails.textContent = dropdownItemDetails?.textContent;

          this.setSelectedDropdownItem(dropdown, dropdownItemVariantId);

          // find the value of data-config-add-on-variant-id and set this.add_on_1_variant_id or this.add_on_2_variant_id to that value
          // const add_on_option = drop_down_item.closest('.DropdownSelector');
          const add_on_index = dropdown.getAttribute('data-config-add-on-index');
          this[`add_on_${add_on_index}_title`] = config_add_on.querySelector('.option-title').textContent.trim();
          this[`add_on_${add_on_index}_variant_id`] = dropdownItemVariantId;

          console.log({
            add_on_index,
            add_on_title: this[`add_on_${add_on_index}_title`],
            add_on_variant_id: this[`add_on_${add_on_index}_variant_id`],
          });
          // clear all hidden inputs in the form and add the add ons to the form
          this.clearAddOnsInForm(this.form).then(() => {
            this.addAddOnsInForm(this.form);
            this.resetSummaryCart().then(() => {
              this.updateSummaryCart();
            });
          });
        });
      }

      setSelectedDropdownItem(dropdown, selectedVariantId) {
        const options = dropdown.querySelectorAll('.DropdownItem');

        options.forEach((option) => {
          option.classList.toggle(
            'selected',
            option.getAttribute('data-config-add-on-variant-id') === selectedVariantId
          );
        });
      }

      attachConfigSectionListener(config_section) {
        if (this.debug) console.log('attachConfigSectionListener');
        const config_section_buttons = config_section.querySelectorAll('[data-button-state]');
        config_section_buttons.forEach((config_button) => {
          config_button.addEventListener('click', (event) => {
            event.preventDefault();
            // Handle the click event
            // if parent [data-section-state] is not "active" then do nothing.
            if (config_section.getAttribute('data-section-state') !== 'active') {
              return;
            }
            // config_button not have attribute data-button-state='active' (to prevent double click or listeners for active button)
            if (
              config_button.hasAttribute('data-button-state') &&
              config_button.getAttribute('data-button-state') !== 'active'
            ) {
              config_section_buttons.forEach((config_button) => {
                // change value of data-button-state to "inactive"
                config_button.setAttribute('data-button-state', 'inactive');
              });
              // change value of data-button-state to "active"

              if (!config_button.classList.contains('AddOnDropdown')) {
                config_button.setAttribute('data-button-state', 'active');
              } else {
                if (config_button.hasAttribute('data-is-item-selected')) {
                  config_button.setAttribute('data-button-state', 'active');
                } else {
                  config_button.setAttribute('data-button-state', 'initial');
                }
              }

              if (!this.disable_pickers) {
                if (config_section.tagName === 'CONFIG-MODEL') {
                  // reset all other filter values that come after this one except finishes
                  this.quantity = false;
                  this.customize = false;
                  this.bundle = false;
                  // set this.product_id to the value of data-config-product-id
                  this.product_id = config_button.getAttribute('data-config-product-id');
                  this.model = config_button.getAttribute('data-config-model');
                  this.resetAddOns();
                  publish(PUB_SUB_EVENTS.productChange);
                  setTimeout(() => this.setProgressBar());
                } else if (config_section.tagName === 'CONFIG-FINISH') {
                  // reset all other filter values that come after this one
                  this.quantity = false;
                  this.customize = false;
                  this.bundle = false;
                  const data_config_finish = config_button.getAttribute('data-config-finish');
                  this.finish = data_config_finish;
                  this.resetAddOns();
                  this.resetAddonsDropdown();
                  this.updateSelectedFinishMeta();
                  setTimeout(() => this.setProgressBar());
                } else if (config_section.tagName === 'CONFIG-QUANTITY') {
                  // reset all other filter values that come after this one
                  this.customize = false;
                  this.bundle = false;
                  const data_config_quantity = config_button.getAttribute('data-config-quantity');
                  this.quantity = data_config_quantity;
                  this.resetAddOns();
                  this.setCustomizeSwiperSlidesNumber();
                  publish(PUB_SUB_EVENTS.productChange);
                } else if (config_section.tagName === 'CONFIG-CUSTOMIZE') {
                  // reset all other filter values that come after this one
                  this.bundle = false;
                  // this.quantity is equal to the value of data-config-quantity on the config-model that is active
                  const config_model = this.querySelector('config-model');
                  const data_config_quantity = config_model
                    .querySelector('[data-button-state="active"]')
                    .getAttribute('data-config-quantity');
                  this.quantity = data_config_quantity;
                  this.resetAddOns();
                  this.setCustomizeSwiperSlidesNumber();
                  const data_config_customize = config_button.getAttribute('data-config-customize');
                  this.customize = data_config_customize;
                  if (data_config_customize === 'true') this.setupSwiperCustomizeSlider();
                } else if (config_section.tagName === 'CONFIG-BUNDLE') {
                  const data_config_bundle = config_button.getAttribute('data-config-bundle');
                  this.bundle = data_config_bundle;
                  this.resetAddOns();
                }
              }
              if (config_section.tagName === 'CONFIG-ADD-ON') {
                // this is a special case, as we need to listen for a lot of different clicks within this section
                // we find all values of data-config-add-on-variant-id with config_button.getAttribute('data-button-state') === 'active'
                // and set this.add_on_1_variant_id and this.add_on_2_variant_id to the values of those buttons
                // then we clear all hidden inputs in the form and add the add ons to the form
                // if data-config-add-on-index="1" then set this.add_on_1_variant_id
                // if data-config-add-on-index="2" then set this.add_on_2_variant_id

                // get all data-button-state="active" elements
                const active_config_buttons = config_section.querySelectorAll('[data-button-state="active"]');
                active_config_buttons.forEach((config_button) => {
                  const add_on_index = config_button.getAttribute('data-config-add-on-index');
                  // get the value of data-config-add-on-variant-id
                  if (config_button.hasAttribute('data-config-add-on-variant-id')) {
                    // how do i reference this.add_on_1_variant_id or this.add_on_2_variant_id dynamically?
                    this[`add_on_${add_on_index}_variant_id`] = config_button.getAttribute(
                      'data-config-add-on-variant-id'
                    );
                  } else {
                    this[`add_on_${add_on_index}_variant_id`] = false;
                  }
                  this.clearAddOnsInForm(this.form).then(() => {
                    this.addAddOnsInForm(this.form);
                    this.resetSummaryCart().then(() => {
                      this.updateSummaryCart();
                    });
                  });
                });
                // add AddOn to the add to cart form and summary cart
              }

              // see if we had picked all of our add on options.
              const config_add_ons = this.querySelectorAll('config-add-on');
              let config_all_add_ons_active = true;

              config_add_ons.forEach((config_add_on) => {
                if (config_add_on.getAttribute('data-section-state') !== 'active') {
                  config_all_add_ons_active = false;
                }
              });
              // if all add ons are not yet active or we're not in the add on section, then we need to set the next section to active
              if (!config_all_add_ons_active || config_section.tagName !== 'CONFIG-ADD-ON') {
                let sibling = config_section.nextElementSibling;
                let active_finish = null;
                let next_section_after_finishes = null;

                while (sibling) {
                  if (
                    sibling.tagName !== 'CONFIG-CART-SUMMARY' &&
                    !sibling.classList.contains('product-carousel-container')
                  ) {
                    // exclude moved mobile sticky swiper
                    if (sibling.tagName === 'CONFIG-FINISH') {
                      if (this.debug) console.log("sibling.tagName === 'CONFIG-FINISH'");
                      // tries to find the same finish button and keep it active to prevent finding variants twice
                      this.handleFinishSectionUpdate(sibling);
                      next_section_after_finishes = sibling.nextElementSibling;
                      active_finish = this.getActiveFinish(sibling);
                    } else if (sibling.tagName === next_section_after_finishes?.tagName) {
                      if (this.debug) console.log('sibling.tagName === next_section_after_finishes?.tagName');
                      if (this.debug) console.log('active_finish', active_finish);

                      if (active_finish?.classList.contains('finish--oos')) {
                        this.setInactiveSection(sibling);
                      } else {
                        if (sibling.tagName === 'CONFIG-CUSTOMIZE') {
                          this.handleCustomizeSectionUpdate(sibling);
                          break;
                        } else {
                          this.setActiveSection(sibling);
                          this.setSiblingsToInitialState(sibling.nextElementSibling);
                        }
                      }

                      break;
                    } else if (sibling.tagName !== next_section_after_finishes?.tagName) {
                      if (this.debug) console.log('sibling.tagName !== next_section_after_finishes?.tagName');

                      active_finish = this.getActiveFinish();
                      if (this.debug) console.log('active_finish updated', active_finish);

                      if (active_finish?.classList.contains('finish--oos')) {
                        this.setInactiveSection(sibling);
                      } else {
                        if (sibling.tagName === 'CONFIG-CUSTOMIZE') {
                          this.handleCustomizeSectionUpdate(sibling);
                          break;
                        } else {
                          this.setInactiveSection(sibling);
                        }
                      }
                    }
                  }
                  sibling = sibling.nextElementSibling;
                }
                // loop through all config_section netxElementSiblings and get the first one which isn't display = none
                // this is for when we need to hide the bundle option for 9oz models of soap
                let next_sibling = config_section.nextElementSibling;

                while (next_sibling) {
                  // this solves the issue when coming from a variant and its add on variant being both out of stock
                  if (next_sibling.tagName === 'CONFIG-ADD-ON' && !active_finish?.classList.contains('finish--oos')) {
                    this.setSectionState(next_sibling, 'active');
                    break;
                  } else if (
                    !next_sibling.classList.contains('tw-hidden') &&
                    !active_finish?.classList.contains('finish--oos') &&
                    next_sibling.tagName !== 'CONFIG-FINISH' &&
                    !next_sibling.classList.contains('product-carousel-container') // exclude moved mobile sticky swiper
                  ) {
                    this.setSectionState(next_sibling, 'active');
                    break;
                  }
                  next_sibling = next_sibling.nextElementSibling;
                }
              }

              this.findVariant();

              // can I get the next div element that's my sibling?
              // do I need to reset all the other sections to "inactive" and all their choices?
            }
          });
        });
      }

      // TODO: add comment
      updateSelectedFinishMeta() {
        const selectedFinishValueEl = this.querySelector('[data-selected-finish]');

        if (selectedFinishValueEl) {
          selectedFinishValueEl.textContent = this.finish;
        }
      }

      /**
       * Retrieves the currently active finish element within a given parent element.
       * By default, searches in the `<config-finish>` element if no parent is provided.
       *
       * @param {Element} [parentEl=document.querySelector('config-finish')] -
       *        The parent element in which to search for the active finish.
       *        Defaults to the first `<config-finish>` element on the page.
       * @returns {Element|null} -
       *        The element that has the `data-config-finish` attribute and
       *        is marked as active with `data-button-state="active"`,
       *        or `null` if no such element is found.
       */
      getActiveFinish(parentEl = document.querySelector('config-finish')) {
        return parentEl.querySelector(`[data-config-finish][data-button-state="active"]`);
      }

      /**
       * Checks if the product is customized by verifying the saved data in the session storage.
       * This method retrieves any saved text or icon customizations from the session storage
       * and determines if any of them exist.
       *
       * @returns {string|null} - Returns the customized data if found, otherwise `null`.
       */
      checkIfCustomized() {
        const savedPrText1 = window.sessionStorage.getItem('saved_pr_text_1');
        const savedPrText2 = window.sessionStorage.getItem('saved_pr_text_2');
        const savedPrIcon1 = window.sessionStorage.getItem('saved_pr_icon_1');
        const savedPrIcon2 = window.sessionStorage.getItem('saved_pr_icon_2');

        return savedPrText1 || savedPrText2 || savedPrIcon1 || savedPrIcon2;
      }

      /**
       * Sets the state for a button by updating its `data-button-state` attribute.
       *
       * @param {Element} button - A button element to update.
       * @param {string} state - The state to set for the button (e.g., 'active', 'inactive', etc.).
       */
      setButtonState(button, state) {
        button.setAttribute('data-button-state', state);
      }

      /**
       * Sets the state of a section by setting its `data-section-state` attribute.
       *
       * @param {Element} section - The section element to update.
       * @param {string} state - The state to set for the section (e.g., 'active', 'inactive', etc.).
       */
      setSectionState(section, state) {
        section.setAttribute('data-section-state', state);
      }

      /**
       * Set a section as inactive by updating its state to 'inactive' and resetting the state
       * of all buttons within that section to 'initial'.
       *
       * @param {Element} sibling - The section element to process as inactive.
       */
      setInactiveSection(sibling) {
        if (this.debug) console.log('setInactiveSection', sibling);
        this.setSectionState(sibling, 'inactive');
        const buttons = sibling.querySelectorAll('[data-button-state]');
        buttons.forEach((button) => {
          this.setButtonState(button, 'initial');
        });
      }

      /**
       * Activates the provided sibling section by setting its state to 'active'
       * and resetting all button states within the section to 'initial'.
       *
       * @param {Element} sibling - The section element to activate.
       */
      setActiveSection(sibling) {
        if (this.debug) console.log('setActiveSection', sibling);
        this.setSectionState(sibling, 'active');
        const buttons = sibling.querySelectorAll('[data-button-state]');
        buttons.forEach((button) => {
          this.setButtonState(button, 'initial');
        });
      }

      /**
       * Handles updating the finish section when a sibling section is activated or deactivated.
       * If there is a previously selected finish button, the section is activated,
       * and all buttons within the section are updated based on the state of the finish button.
       *
       * @param {Element} sibling - The sibling section element that needs its finish state updated.
       */
      handleFinishSectionUpdate(sibling) {
        const previousSelectedFinishButton = sibling.querySelector(
          `[data-config-finish="${this.finish}"][data-config-model="${this.model}"]`
        );
        if (this.debug) console.log('previousSelectedFinishButton', previousSelectedFinishButton);

        if (previousSelectedFinishButton) {
          this.setSectionState(sibling, 'active');
          sibling.querySelectorAll('[data-button-state]').forEach((configButton) => {
            const state = configButton === previousSelectedFinishButton ? 'active' : 'initial';
            this.setButtonState(configButton, state);
          });
        }
      }

      /**
       * Handles updating the customize section based on whether the configuration
       * has been customized. If a customized configuration is detected, inactive the
       * "customize = false" button and activate the "customize = true" button. It then
       * updates the model's state (quantity, customize, and bundle) and finds the
       * appropriate variant. Finally, it activates the next section if customized;
       * otherwise, it keeps the current section active.
       *
       * @param {Element} sibling - The customize section element to process.
       */
      handleCustomizeSectionUpdate(sibling) {
        const isCustomized = this.checkIfCustomized();

        if (isCustomized) {
          this.setButtonState(sibling.querySelector('[data-config-customize="false"]'), 'inactive');

          const customizeButton = sibling.querySelector('[data-config-customize="true"]');
          this.setButtonState(customizeButton, 'active');

          const configModel = this.querySelector('config-model');
          this.quantity = configModel
            .querySelector('[data-button-state="active"]')
            .getAttribute('data-config-quantity');
          this.customize = customizeButton.getAttribute('data-config-customize');
          this.bundle = false;

          this.findVariant();

          this.setActiveSection(sibling.nextElementSibling);
        } else {
          this.setActiveSection(sibling);
          this.setSiblingsToInitialState(sibling.nextElementSibling);
        }
      }

      /**
       * Sets all sibling sections starting from the provided sibling to an initial state.
       * Iterates through all next sibling elements and deactivates their sections unless
       * the sibling is one with a tagName of 'CONFIG-CART-SUMMARY'.
       *
       * @param {Element} startingSibling - The DOM element from which to begin setting siblings to initial state.
       */
      setSiblingsToInitialState(startingSibling) {
        let siblingToBeInactive = startingSibling;
        while (siblingToBeInactive) {
          if (siblingToBeInactive.tagName !== 'CONFIG-CART-SUMMARY') {
            if (this.debug) console.log('while (siblingToBeInactive)');
            this.setInactiveSection(siblingToBeInactive);
          }
          siblingToBeInactive = siblingToBeInactive.nextElementSibling;
        }
      }

      resetFilterValues() {
        this.finish = false;
        this.quantity = false;
        this.customize = false;
        this.bundle = false;
      }

      findVariant() {
        // find the variant within the json and update the variant loaded in the page
        if (this.debug) console.log('findVariant');
        if (this.debug) console.log('this.model: ', this.model);
        if (this.debug) console.log('this.finish: ', this.finish);
        if (this.debug) console.log('this.quantity: ', this.quantity);
        if (this.debug) console.log('this.customize: ', this.customize);
        if (this.debug) console.log('this.bundle: ', this.bundle);

        let json_config_product_variant = null;

        if (this.model && this.finish && this.quantity && this.customize && this.bundle) {
          if (this.debug) console.log('try to find with model, finish, quantity, customize, and bundle');
          json_config_product_variant = document.querySelector(
            `script[data-json-config-model="${this.model}"][data-json-config-pack-size="${this.quantity}"][data-json-config-is-customize-option="${this.customize}"][data-json-config-is-bundle-option="false"]`
          );
          if (json_config_product_variant) {
            // update the variant loaded in the page
            const this_finish = this.finish;
            this.updateSelectedVariant(json_config_product_variant, 'finish', this_finish);
            // this.updateSpecsModal(json_config_product_variant);
          }
        } else if (this.model && this.finish && this.quantity && this.customize) {
          if (this.debug) console.log('try to find with model, finish, quantity, and customize');
          json_config_product_variant = document.querySelector(
            `script[data-json-config-model="${this.model}"][data-json-config-pack-size="${this.quantity}"][data-json-config-is-customize-option="${this.customize}"][data-json-config-is-bundle-option="false"]`
          );
          if (json_config_product_variant) {
            // update the variant loaded in the page
            const this_finish = this.finish;
            this.updateSelectedVariant(json_config_product_variant, 'finish', this_finish);
          }
        } else if (this.model && this.finish && this.quantity) {
          if (this.debug) console.log('try to find with product, finish, and quantity');
          if (this.debug) console.log('this.model: ', this.model);
          if (this.debug) console.log('this.finish: ', this.finish);
          if (this.debug) console.log('this.quantity: ', this.quantity);
          json_config_product_variant = document.querySelector(
            `script[data-json-config-model="${this.model}"][data-json-config-pack-size="${this.quantity}"][data-json-config-is-bundle-option="false"]`
          );
          if (json_config_product_variant) {
            // update the variant loaded in the page
            const this_finish = this.finish;
            this.updateSelectedVariant(json_config_product_variant, 'finish', this_finish);
            // this.updateSpecsModal(json_config_product_variant);
          }
        } else if (this.model && this.finish) {
          if (this.debug) console.log('try to find with model and finish');
          if (this.debug) console.log('this.model: ', this.model);
          if (this.debug) console.log('this.product_id: ', this.product_id);
          if (this.debug) console.log('this.finish: ', this.finish);
          // find json script that has attribute data-json-config-product-id="{{this.product_id}}" and data-json-config-finish="{{this.finish}} and parse the JSON to get the data within it
          /*
          const json_config_product_variant = document.querySelector(
            `script[data-json-config-product-id="${this.product_id}"][data-json-config-finish="${this.finish}"]`
          );
*/
          json_config_product_variant = document.querySelector(`script[data-json-config-model="${this.model}"]`);

          if (json_config_product_variant) {
            // update the variant loaded in the page
            const this_finish = this.finish;
            this.updateSelectedVariant(json_config_product_variant, 'finish', this_finish);
            // this.updateSpecsModal(json_config_product_variant);
          }
        } else if (this.finish) {
          if (this.debug) console.log('try to find with finish');
          if (this.debug) console.log('this.product_id: ', this.product_id);
          if (this.debug) console.log('this.finish: ', this.finish);
          json_config_product_variant = document.querySelector(
            `script[data-json-config-product-id="${this.product_id}"]`
          );

          if (json_config_product_variant) {
            // update the variant loaded in the page
            const this_finish = this.finish;
            this.updateSelectedVariant(json_config_product_variant, 'finish', this_finish);
            // this.updateSpecsModal(json_config_product_variant);
          }
        } else {
          if (this.debug) console.log('just use the product id to find the variant');
          // find json script that has attribute data-json-config-variant-sku="model" and parse the JSON to get the data within it
          json_config_product_variant = document.querySelector(
            `script[data-json-config-product-id="${this.product_id}"]`
          );
          if (json_config_product_variant) {
            // update the variant loaded in the page
            this.updateSelectedVariant(json_config_product_variant);
            // this.updateSpecsModal(json_config_product_variant);
          }
        }
        this.updateURL();
        this.updateSelectedVariantJsonData(json_config_product_variant);
      }

      resetAddOns() {
        // reset all addon_#_variant_id
        for (let i = 1; i <= this.num_add_ons; i++) {
          this[`add_on_${i}_variant_id`] = false;
        }
      }

      resetAddonsDropdown() {
        const dropdowns = this.querySelectorAll('.AddOnDropdown');

        if (dropdowns.length === 0) return;

        dropdowns.forEach((dropdown) => {
          const initialTitle = dropdown.getAttribute('data-add-on-dropdown-title');
          const initialSubtitle = dropdown.getAttribute('data-add-on-dropdown-subtitle');

          const selectorTitleEl = dropdown.querySelector('.DropdownSelector .option-button-text');
          const selectorDetailsEl = dropdown.querySelector('.DropdownSelector .option-btn-sub-text');

          if (initialTitle && selectorTitleEl) {
            selectorTitleEl.textContent = initialTitle;
          }
          if (initialSubtitle && selectorDetailsEl) {
            selectorDetailsEl.textContent = initialSubtitle;
          }

          dropdown.removeAttribute('data-is-item-selected');

          const selectedOption = dropdown.querySelector('.DropdownItem.selected');
          if (selectedOption) {
            selectedOption.classList.remove('selected');
          }
        });
      }

      updateSelectedVariant(json_config_product_variant, selector = 'variant', selector_value = false) {
        // find the variant within the json and update the variant loaded in the page which includes swapping out the media gallery at the top of the page, updating the price, updating the summary cart, and updating the hidden inputs in the form
        if (this.debug) console.log('updateSelectedVariant');

        this.setConfiguarableProductValues(json_config_product_variant, selector, selector_value);

        this.hideConfigBundle();
        this.setupFinishPicker();
        this.initializeSwipers();
        this.updateSpecsModal();

        this.updateAddOnItems();
        // Update inputs in the form
        this.updateAddToCartForm(this.form);

        this.clearAddOnsInForm(this.form).then(() => {
          this.resetSummaryCart().then(() => {
            this.updateSummaryCart();
          });
        });
        this.showSpecsModalOpener();
        this.updateProductFeatures();
      }

      /**
       * Updates the data for the selected variant by modifying the `data-selected-variant` element's content.
       * Parses the JSON configuration for the product, finds the current variant, and updates its schema.
       *
       * @param {HTMLElement} productJsonDataEl - The HTML element containing the JSON data of the product.
       * @returns {void}
       */
      updateSelectedVariantJsonData(productJsonDataEl) {
        const dataSelectedVariantEl = document.querySelector('[data-selected-variant]');
        const thisVariantId = this.variant_id;

        if (!dataSelectedVariantEl || !thisVariantId) return;

        const productJsonData = JSON.parse(productJsonDataEl.innerHTML);

        const selectedVariantJsonData = productJsonData?.variants?.find(function (variant) {
          return variant.id === thisVariantId;
        });

        if (!selectedVariantJsonData) return;

        const selectedVariantSchema = {
          id: selectedVariantJsonData.id,
          name: selectedVariantJsonData.name,
          featured_image: {
            src: selectedVariantJsonData.featured_image,
          },
          price: selectedVariantJsonData.price,
          compare_at_price: selectedVariantJsonData.compare_at_price,
        };

        dataSelectedVariantEl.innerHTML = JSON.stringify(selectedVariantSchema);
      }

      hideConfigBundle() {
        // special case for hiding config-bundle for 9 oz models
        const config_bundle = this.querySelector('config-bundle');
        if (!config_bundle) return;

        if (this.model === '9 OZ') {
          config_bundle.classList.add('tw-hidden');
        } else {
          config_bundle.classList.remove('tw-hidden');
        }
      }
      initializeSwipers() {
        if (this.debug) console.log('initializeSwipers');

        const isCustomized = this.checkIfCustomized();
        let productId;
        let variantId;
        if (isCustomized) {
          const activeFinish = this.getActiveFinish();
          productId = activeFinish.dataset.configProductId;
          variantId = activeFinish.dataset.configVariantId;
          if (this.debug) console.log('productId from isCustomized', productId);
          if (this.debug) console.log('variantId from isCustomized', variantId);
        } else {
          productId = this.product_id;
          variantId = this.variant_id;
        }

        const selectedProductSwiper = this.swipers[productId];
        // no need to update sliders because the slider for the selected option does not exist
        if (!selectedProductSwiper) {
          if (this.debug) console.warn(`Swiper for product ID ${productId} not found.`);
          return;
        }

        // Check if the swiper element has display: block
        const swiperElement = selectedProductSwiper.el;
        const computedStyle = window.getComputedStyle(swiperElement);

        if (computedStyle.display === 'block') {
          if (this.debug)
            console.log(`Skipping initialization for Swiper with display: block for product ${productId}.`);
          this.updateSwiperSlider(selectedProductSwiper, productId, variantId);
          return;
        }

        const allSwipers = document.querySelectorAll('.swiper-product-images-container');

        allSwipers.forEach((swiper) => {
          swiper.style.display = 'none';
          swiper.parentElement.querySelector('.swiper-pagination').style.display = 'none';
        });

        this.updateSwiperSlider(selectedProductSwiper, productId, variantId);
      }

      updateAddOnItems() {
        // if (this.debug) {
        // console.log('updateAddOnItems');
        // for (let i = 1; i <= this.num_add_ons; i++) {
        //   console.log(`this.add_on_${i}_product_id:`, this[`add_on_${i}_product_id`]);
        // }
        // }

        // Hide all config-add-on elements
        const configAddOns = this.querySelectorAll('config-add-on');
        configAddOns.forEach((configAddOn) => {
          configAddOn.classList.add('tw-hidden');
        });

        // Hide all add-on items
        const addOnItems = document.querySelectorAll('div[data-config-add-on-product-id]');
        addOnItems.forEach((addOnItem) => {
          addOnItem.style.display = 'none';
        });

        // Helper function to display add-on items and their parent
        const showAddOnItem = (productId) => {
          if (!productId) return;

          const addOnItem = document.querySelector(`div[data-config-add-on-product-id="${productId}"]`);
          if (!addOnItem) return;

          addOnItem.style.display = 'inline-flex';

          const parentConfigAddOn = addOnItem.closest('config-add-on');
          if (parentConfigAddOn) {
            parentConfigAddOn.classList.remove('tw-hidden');
          }
        };

        // Show relevant add-on items
        // showAddOnItem(this.add_on_1_product_id);
        // showAddOnItem(this.add_on_2_product_id);
        for (let i = 1; i <= this.num_add_ons; i++) {
          showAddOnItem(this[`add_on_${i}_product_id`]);
        }

        // Handle unavailable variant
        if (this.variant_available === 'false') {
          configAddOns.forEach((configAddOn) => {
            configAddOn.setAttribute('data-section-state', 'inactive');
          });
        }

        this.matchAddOnFinish();
      }

      matchAddOnFinish() {
        const addOnToMatches = document.querySelectorAll('[data-config-add-on-match-finish]');
        for (const addOnToMatch of addOnToMatches) {
          const add_on_product_id = addOnToMatch.getAttribute('data-config-add-on-product-id');

          let configProductHasTheAddon = false;

          for (let i = 1; i <= this.num_add_ons; i++) {
            if (this[`add_on_${i}_product_id`] === add_on_product_id) {
              configProductHasTheAddon = true;
              break;
            }
          }

          if (!configProductHasTheAddon) {
            if (this.debug)
              console.log(`Skipping add-on ${add_on_product_id} - not related to the active config product`);
            continue;
          }

          const addOnsToMatchParent = addOnToMatch.closest('config-add-on');
          addOnsToMatchParent?.classList.add('tw-hidden');

          const matchedFinishAddOnVariantData = document.querySelector(
            `[data-json-addon-product-id="${add_on_product_id}"][data-json-addon-variant-option-1="${this.finish}"]`
          );

          if (matchedFinishAddOnVariantData) {
            const addonVariantSku = matchedFinishAddOnVariantData.dataset.jsonAddonVariantSku;
            const variantAddOnImageMappings = document.querySelector('[data-variant-add-on-image-mappings]');
            let mappingImageFound = false;

            if (variantAddOnImageMappings) {
              try {
                const mappingsData = JSON.parse(variantAddOnImageMappings.innerHTML);
                const searchHandle = `${this.variant_sku}-${addonVariantSku}`.toLowerCase();

                // Find the matching mapping in the mappings array
                const variantAddOnImageMapping = mappingsData?.mappings?.find(
                  (mapping) => mapping.handle === searchHandle
                );

                // If we found a mapping, update the image
                if (variantAddOnImageMapping?.image) {
                  // if variantAvailable is true, show the add-on
                  const variantAvailable = matchedFinishAddOnVariantData.dataset.jsonAddonVariantAvailable;
                  if (variantAvailable === 'true') {
                    addOnsToMatchParent?.classList.remove('tw-hidden');
                  }

                  const variantImgEl = document.querySelector(
                    `[data-preview-image-product-id="${add_on_product_id}"] [data-addon-preview-image]`
                  );
                  if (variantImgEl) {
                    variantImgEl.src = variantAddOnImageMapping.image;
                    variantImgEl.setAttribute('data-src', variantAddOnImageMapping.image);
                    mappingImageFound = true;
                  }
                }
              } catch (error) {
                console.warn('Error parsing variant add-on image mappings:', error);
                console.warn('Raw mappings data:', variantAddOnImageMappings.innerHTML);
              }
            }

            if (!addOnToMatch) {
              if (this.debug) console.log('No add-ons to match finish');
              continue;
            } else if (!matchedFinishAddOnVariantData) {
              const addOnsToMatchParent = addOnToMatch.closest('config-add-on');
              addOnsToMatchParent?.classList.add('tw-hidden');

              if (this.debug) console.log('No available add-ons found.');
              continue;
            }

            let matchedFinishAddOnDataJson = null;

            try {
              matchedFinishAddOnDataJson = JSON.parse(matchedFinishAddOnVariantData.innerHTML);
              if (this.debug) console.log('Parsed matchedFinishAddOnData:', matchedFinishAddOnDataJson);
            } catch (error) {
              if (this.debug) console.error('Error parsing matchedFinishAddOnData:', error);
              continue;
            }

            const optionNameTextEl = addOnToMatch.querySelector('.OptionBtnText-wrapper .option-btn-sub-text');
            const variantImgEl = document.querySelector(
              `[data-preview-image-product-id="${add_on_product_id}"] [data-addon-preview-image]`
            );

            const option1Value = matchedFinishAddOnDataJson.options[0];
            const variantPrice = matchedFinishAddOnDataJson.price;
            const newVariantImgSrc = matchedFinishAddOnVariantData.dataset.jsonAddonOverrideImage;

            // set variant id
            addOnToMatch.setAttribute('data-config-add-on-variant-id', matchedFinishAddOnDataJson.id);

            // update option name and price
            if (optionNameTextEl && option1Value) {
              const textParts = optionNameTextEl.textContent.split('|');
              textParts[0] = option1Value;
              textParts[1] = `${window.currencyCode}${variantPrice / 100}`;
              optionNameTextEl.textContent = textParts.join(' | ');
            }

            // Only update image with override if no mapping was found
            if (!mappingImageFound && variantImgEl && newVariantImgSrc) {
              variantImgEl.src = newVariantImgSrc;
              variantImgEl.setAttribute('data-src', newVariantImgSrc);
            }
          }
        }
      }

      updateAddToCartForm(form) {
        if (this.debug) console.log('updateAddToCartForm, variant_id: ', this.variant_id);

        if (form) {
          // Find the hidden input field
          const hiddenInput = form.querySelector('input[name="id"]');
          // Assuming this.variant_id is defined and holds the correct variant ID
          const variantId = this.variant_id; // Make sure this is defined correctly
          // Update the hidden input's value
          if (hiddenInput) {
            hiddenInput.value = variantId; // Update the hidden input value
          }
          // fire custom event that others can listen to for a cart update
          document.dispatchEvent(new CustomEvent('cartUpdated'));
        }
      }

      clearAddOnsInForm = (form) => {
        if (this.debug) console.log('clearHiddenInputsToForm()');
        return new Promise((resolve, reject) => {
          if (!form) {
            console.error('Form with .add-to-cart-form not found.');
            return reject(new Error('Form not found'));
          }

          // Clear previous hidden inputs
          const hiddenInputs = form.querySelectorAll('.pdp_addons');
          hiddenInputs.forEach((input) => input.remove());

          if (this.debug) console.log('Previous hidden inputs cleared.');
          resolve();
        });
      };

      addAddOnsInForm = (form) => {
        if (this.debug) console.log('addAddOnsInForm()');
        return new Promise((resolve, reject) => {
          if (!form) {
            console.error('Form with .add-to-cart-form not found.');
            return reject(new Error('Form not found'));
          }

          // Create hidden inputs for the selected add-ons
          const hiddenInputs = [];
          let index = 0;
          for (let i = 1; i <= this.num_add_ons; i++) {
            if (this[`add_on_${i}_variant_id`]) {
              hiddenInputs.push(this.createHiddenInput(`items[${index}][id]`, this[`add_on_${i}_variant_id`]));
              hiddenInputs.push(this.createHiddenInput(`items[${index}][quantity]`, 1));
              index++;
            }
            // Append the hidden inputs to the form
            hiddenInputs.forEach((input) => form.appendChild(input));
          }
          if (this.debug) console.log('Hidden inputs added to the form.');
          resolve();
        });
      };
      createHiddenInput = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.classList.add('pdp_addons');
        return input;
      };
      updateSummaryCart() {
        if (this.debug) console.log('updateSummaryCart');
        const selected_product_variant_json = document.querySelector(
          `script[data-json-config-product-id="${this.product_id}"]`
        );
        const this_variant_id = this.variant_id;
        //update price in cart for all items in form
        const selected_variant_data = JSON.parse(selected_product_variant_json.innerHTML);
        const targetVariant = selected_variant_data.variants.find(function (variant) {
          return variant.id === this_variant_id;
        });
        /*
{
            "id": "42226361925734",
            "sku": "ST1069SET2",
            "finish": "brushed",
            "price": "17000",
            "available": "true",
            "options": ["14 oz. 2-pack","brushed"]
          }
*/
        const variant_options = targetVariant.options;
        // loop through options to get the subtext
        let variant_option_subtext = '';
        variant_options.forEach((option) => {
          // if option has 9 oz. or 14 oz. in it, option should just be "9 oz." or "14 oz."
          if (option.includes('9 oz.')) {
            option = '9 oz.';
          } else if (option.includes('14 oz.')) {
            option = '14 oz.';
          }
          variant_option_subtext += option;
          // add a " | " after each option which isn't the last one
          if (variant_options.indexOf(option) !== variant_options.length - 1) {
            variant_option_subtext += ' | ';
          }
        });
        if (this.customize === 'true') {
          variant_option_subtext += ' | Personalized';
        }
        // strip all ":" from the variant_option_subtext
        variant_option_subtext = variant_option_subtext.replace(/:/g, '');

        this.updateSummaryCartItem('.CartItem-container--main', this.cart_title, variant_option_subtext);

        let cart_summary_total = targetVariant.price / 100;
        let cart_summary_compare_at_total = targetVariant.compare_at_price / 100;

        // loop through this.form for any ".pdp_addons" and add them to the summary
        let addon_index = 1;
        this.form.querySelectorAll('.pdp_addons').forEach((hidden_input) => {
          // if the name has id in it
          if (hidden_input.name.includes('id')) {
            const add_on_variant_id = hidden_input.value;
            const add_on_variant_json = document.querySelector(
              `script[data-json-addon-variant-id="${add_on_variant_id}"]`
            );
            const add_on_title = add_on_variant_json.getAttribute('data-json-addon-cart-title');
            let add_on_title_subtext = add_on_variant_json.getAttribute('data-json-addon-cart-title-subtext');
            const add_on_option = add_on_variant_json.getAttribute('data-json-addon-cart-option');
            // const add_on_variant_option_1 = add_on_variant_json.getAttribute('data-json-addon-variant-option-1');
            // const add_on_variant_option_2 = add_on_variant_json.getAttribute('data-json-addon-variant-option-2');

            // if (add_on_variant_option_2) {
            //   add_on_title_subtext = add_on_title_subtext + ' | ' + add_on_variant_option_2;
            // }

            this.updateSummaryCartItem(
              `.CartItem-container--addon${addon_index}`,
              add_on_title,
              add_on_title_subtext,
              add_on_variant_id
            );

            const add_on_variant_json_data = JSON.parse(add_on_variant_json.innerHTML);
            cart_summary_total += add_on_variant_json_data.price / 100;
            cart_summary_compare_at_total += add_on_variant_json_data.compare_at_price / 100;
            addon_index++;
          }
        });

        const cart_total_value = document.querySelector('.CartTotal .CartTotal-value');
        // set the cart total value to the price of the selected variant, but don't include cents if there are none
        cart_total_value.textContent = this.formatCurrency(cart_summary_total);
        const cart_total_compare_at_price_value = document.querySelector(
          '.CartTotal .CartTotal-value--compare-at-price'
        );
        cart_total_compare_at_price_value.textContent = this.formatCurrency(cart_summary_compare_at_total);
        if (cart_summary_compare_at_total > cart_summary_total) {
          cart_total_compare_at_price_value.style.display = 'block';
        } else {
          cart_total_compare_at_price_value.style.display = 'none';
        }

        // don't show config-cart-summary unless this.finish is not false
        const config_cart_summary = document.querySelector('config-cart-summary');
        // if this.finish is not false, then show the config_cart_summary
        // find if any button within config-finish has data-button-state="active"
        const config_finish = document.querySelector('config-finish');
        const config_finish_buttons = config_finish.querySelectorAll('[data-button-state="active"]');
        const add_to_cart_button = document.querySelector('product-form button');
        const notify_me_button = document.querySelector('config-cart-summary [data-notify-btn]');

        if (this.variant_available === 'false') {
          if (this.hide_notify_me === 'true') {
            add_to_cart_button.classList.remove('!tw-hidden');
            notify_me_button?.classList.add('hidden');
          } else {
            add_to_cart_button.classList.add('!tw-hidden');
            notify_me_button?.classList.remove('hidden');
            notify_me_button?.setAttribute('data-variant-id', targetVariant.id);
          }

          add_to_cart_button.setAttribute('disabled', 'disabled');
          add_to_cart_button.querySelector('.AddToCart').textContent = window.variantStrings.soldOut;
        } else if (config_finish_buttons.length > 0) {
          config_cart_summary.classList.remove('!tw-opacity-0');
          config_cart_summary.classList.add('!tw-opacity-100');
          add_to_cart_button.removeAttribute('disabled');
          add_to_cart_button.querySelector('.AddToCart').textContent = window.variantStrings.addToCart;
          add_to_cart_button.classList.remove('!tw-hidden');
          notify_me_button?.classList.add('hidden');
        } else {
          config_cart_summary.classList.remove('!tw-opacity-100');
          config_cart_summary.classList.add('!tw-opacity-0');
          add_to_cart_button.removeAttribute('disabled');
          add_to_cart_button.querySelector('.AddToCart').textContent = window.variantStrings.addToCart;
          add_to_cart_button.classList.remove('!tw-hidden');
          notify_me_button?.classList.add('hidden');
        }

        this.updateDeliveryTime(targetVariant);

        // fire add-to-cart-form
        document.dispatchEvent(new CustomEvent('add-to-cart-form:updated'));
        // update modal upsell

        // Get the stored handle from sessionStorage
        // const storedHandle = sessionStorage.getItem('handle'); // TODO: if need to use, refactor part with session storage there and in updateProductFeatures
        //
        // // If the handle is not the same as the stored one or it's not stored, perform the request
        // if (this.handle !== undefined && this.handle !== storedHandle) {
        //   // Save the new handle value to sessionStorage
        //   sessionStorage.setItem('handle', this.handle);
        //
        //   fetch(`/products/${this.handle}?section_id=upsell-items-modal`)
        //     .then((response) => response.text())
        //     .then((data) => {
        //       // Parse the HTML string into a document
        //       const parser = new DOMParser();
        //       const doc = parser.parseFromString(data, 'text/html');
        //
        //       // Find the element with class .upsell-modal__recommend in the new document
        //       const newRecommend = doc.querySelector('.upsell-modal__recommend');
        //
        //       if (newRecommend) {
        //         // Find the current upsell modal recommendation
        //         const currentRecommend = document.querySelector('.upsell-modal__recommend');
        //         if (currentRecommend) {
        //           // Replace the current recommendation with the new one
        //           currentRecommend.replaceWith(newRecommend);
        //
        //           const modalWindow = document.querySelector('modal-window#bis_modal');
        //           if (modalWindow) modalWindow.attachModalListeners();
        //         }
        //       }
        //     })
        //     .catch((error) => console.error('Error:', error));
        // } else {
        //   if (this.debug) console.log("Handle hasn't changed, request not executed.");
        // }

        // -- end update modal upsell
      }

      resetSummaryCart = () => {
        if (this.debug) console.log('resetSummaryCart()');
        return new Promise((resolve, reject) => {
          if (!this.form) {
            console.error('Form with .add-to-cart-form not found.');
            return reject(new Error('Form not found'));
          }
          this.updateSummaryCartItem('.CartItem-container--main', '', '');
          // this.updateSummaryCartItem('.CartItem-container--addon1', '', '');
          // this.updateSummaryCartItem('.CartItem-container--addon2', '', '');
          for (let i = 1; i <= this.num_add_ons; i++) {
            this.updateSummaryCartItem(`.CartItem-container--addon${i}`, '', '');
          }
          const cart_total_value = document.querySelector('.CartTotal .CartTotal-value');
          const cart_total_compare_at_pricevalue = document.querySelector(
            '.CartTotal .CartTotal-value--compare-at-price'
          );
          cart_total_value.textContent = '';
          cart_total_compare_at_pricevalue.textContent = '';
          if (this.debug) console.log('Previous items in summary cart cleared.');
          resolve();
        });
      };

      updateSummaryCartItem(parent_selector, title, details, variant_id = false) {
        const cart_item_title = document.querySelector(`${parent_selector} .CartItemTitle`);
        if (cart_item_title) cart_item_title.innerHTML = title.replace(/&amp;/g, '&');
        const cart_item_details = document.querySelector(`${parent_selector} .CartItemDetails`);
        if (cart_item_details) cart_item_details.textContent = details;
        if (variant_id) {
          // add data attribute "variant-id" to the parent_selector
          const parent_selector_element = document.querySelector(parent_selector);
          parent_selector_element.setAttribute('data-variant-id', variant_id);
        }
      }
      setupFinishPicker() {
        // hide all finish pickers first
        const finish_pickers = document.querySelectorAll('[data-config-finish]');
        finish_pickers.forEach((finish_picker) => {
          finish_picker.style.display = 'none';
        });
        // set the finish picker that has this.product_id with style display: flex
        const active_finish_pickers = document.querySelectorAll(
          `[data-config-finish][data-config-model="${this.model}"]`
        );

        active_finish_pickers.forEach((active_finish_picker) => {
          active_finish_picker.style.display = 'inline-flex';
        });

        const selected_finish_picker = document.querySelector(
          `[data-config-finish][data-config-model="${this.model}"][data-button-state="active"]`
        );
        let bool_selected_finish = !!selected_finish_picker;

        if (!bool_selected_finish) {
          // try to find the finish that was previously selected and click it
          const previous_finish = window.localStorage.getItem('previous_finish');
          if (previous_finish) {
            const previous_finish_button = document.querySelector(
              `[data-config-model="${this.model}"][data-config-finish="${previous_finish}"]`
            );
            if (previous_finish_button) {
              previous_finish_button.click();
            } else {
              const first_active_finish = active_finish_pickers[0];
              first_active_finish.click();
              console.log('click first_active_finish');
            }
          }
        }
        window.localStorage.setItem('previous_finish', this.finish);
      }

      setupSwiperSliders() {
        if (this.debug) console.log('setupSwiperSliders');
        const swiperContainers = document.querySelectorAll('.swiper-product-images-container');

        // Create each Swiper instance
        swiperContainers.forEach((container) => {
          const product_id = container.dataset.swiperProductId;

          this.swipers[product_id] = new window.Swiper(container, {
            init: product_id === this.product_id,
            slidesPerView: 1,
            allowTouchMove: true,
            loop: true,
            pagination: {
              el: `.swiper-outer-wrapper .swiper-pagination[data-swiper-pagination-id="${product_id}"] `,
            },
            navigation: {
              nextEl: `.swiper-product-images-container[data-swiper-product-id="${product_id}"] .swiper-button-next`,
              prevEl: `.swiper-product-images-container[data-swiper-product-id="${product_id}"] .swiper-button-prev`,
            },
            breakpoints: {
              820: {
                loop: false,
              },
            },
            on: {
              afterInit: (swiper) => {
                swiper.pagination.el.style.display = 'block';
                swiper.navigation.prevEl.style.visibility = 'visible';
                swiper.navigation.nextEl.style.visibility = 'visible';
              },
              breakpoint: function () {
                this.navigation.update();
              },
              touchMove: function (swiper, event) {
                if (event.touches) {
                  if (window.visualViewport.scale > 1.2) {
                    this.allowSlideNext = false;
                    this.allowSlidePrev = false;
                  }
                }
              },
              touchEnd: function (swiper, event) {
                if (event.touches) {
                  if (window.visualViewport.scale <= 1.2) {
                    this.allowSlideNext = true;
                    this.allowSlidePrev = true;
                  }
                }
              },
            },
          });
        });
      }

      setupSwiperCustomizeSlider() {
        if (this.debug) console.log('setupSwiperCustomizeSlider');
        const swiperContainer = document.querySelector('.swiper-customize-images-container');
        if (!swiperContainer) {
          if (this.debug) console.log('No customize swiper container found');
          return;
        }

        // Check if Swiper instance already exists
        if (window.customizeSwiper && window.customizeSwiper.initialized) {
          if (this.debug) console.log('Swiper instance already exists, skipping initialization, just update');
          window.customizeSwiper.update();
          return;
        }

        window.customizeSwiper = new window.Swiper(swiperContainer, {
          slidesPerView: 'auto',
          pagination: {
            el: '.personalizationNav .swiper-pagination',
            type: 'fraction',
          },
          navigation: {
            nextEl: '.personalizationNav .swiper-button-next',
            prevEl: '.personalizationNav .swiper-button-prev',
          },
          on: {
            slideChange: this.handleSlideChange,
          },
        });
      }

      handleSlideChange() {
        const activeSlideIndex = window.customizeSwiper.activeIndex + 1;
        const textInput = document.querySelector('input[data-input-personalization-text]');
        // get the value within pr_text_activeSlideIndex from session storage and set it to actualText
        if (sessionStorage.getItem(`pr_text_${activeSlideIndex}`)) {
          const activeSlideText = sessionStorage.getItem(`pr_text_${activeSlideIndex}`);
          textInput.value = activeSlideText;
        } else {
          textInput.value = '';
        }
        // set the icon buttons based on the pr_icon_activeSlideIndex from session storage
        const iconButtons = document.querySelectorAll('.personalizationIcons [data-icon-value]');
        iconButtons.forEach((iconButton) => {
          iconButton.setAttribute('data-modal-button-state', 'initial');
        });
        if (sessionStorage.getItem(`pr_icon_${activeSlideIndex}`)) {
          const activeSlideIcon = sessionStorage.getItem(`pr_icon_${activeSlideIndex}`);
          const iconButton = document.querySelector(`[data-icon-value="${activeSlideIcon}"]`);
          iconButtons.forEach((iconButton) => {
            iconButton.setAttribute('data-modal-button-state', 'inactive');
          });
          // set data-modal-button-state to active
          iconButton.setAttribute('data-modal-button-state', 'active');
        }
      }

      updateSwiperSlider(slider, productId, variantId) {
        if (this.debug) console.log('updateSwiperSlider');

        const containerSelector = `.swiper-product-images-container[data-swiper-product-id="${productId}"]`;
        const allVariantImages = Array.from(document.querySelectorAll(`${containerSelector} [data-swiper-variant-id]`));

        const visibleSlides = allVariantImages.filter(
          (img) => img.getAttribute('data-swiper-variant-id') === variantId
        );
        const hiddenSlides = allVariantImages.filter((img) => !visibleSlides.includes(img));

        // Check if all visible slides are correctly configured
        const allSlidesCorrect = visibleSlides.every(
          (slide) => slide.classList.contains('swiper-slide') && !slide.classList.contains('swiper-hidden-slide')
        );

        // Check if slides are in the correct order
        const allInCorrectOrder = allVariantImages.every((slide, index) => {
          if (hiddenSlides.includes(slide)) {
            return index < hiddenSlides.length;
          } else if (visibleSlides.includes(slide)) {
            return index >= hiddenSlides.length;
          }
          return false;
        });

        // If everything is already correct, no need to update
        if (allSlidesCorrect && allInCorrectOrder) {
          this._finalizeSliderUpdate(slider);
          if (this.debug) console.log('no need to update product swiper');
          return;
        }

        // Update slide classes
        allVariantImages.forEach((slide) => {
          const isVisible = visibleSlides.includes(slide);
          slide.classList.toggle('swiper-slide', isVisible);
          slide.classList.toggle('swiper-hidden-slide', !isVisible);
        });

        // Update Swiper instance
        this._updateSliderInstance(slider, visibleSlides);

        // Finalize updates
        this._finalizeSliderUpdate(slider);
      }

      // Private method to handle slider updates and reinitialization logic
      _updateSliderInstance(slider, visibleSlides) {
        if (!slider.initialized) {
          slider.init();
          return;
        }

        if (slider.params.loop) {
          slider.loopDestroy();

          if (visibleSlides.length > 1) {
            // Reorder slides
            const swiperWrapper = slider.wrapperEl;
            if (swiperWrapper) {
              visibleSlides.forEach((slide) => swiperWrapper.appendChild(slide));
            }
            slider.params.loopedSlides = visibleSlides.length;
            slider.loopCreate();
            slider.slideToLoop(0, 0);
          }
        } else {
          slider.slideTo(0, 0);
        }

        slider.update();
        slider.pagination.update();
        slider.navigation.update();
      }

      // Private method to finalize slider update process
      _finalizeSliderUpdate(slider) {
        if (!slider.initialized) slider.init();

        slider.el.style.display = 'block';
        slider.pagination.el.style.display = 'block';

        if (this.debug) console.log('Slider update finalized');
      }

      setCustomizeSwiperSlidesNumber() {
        if (this.debug) console.log('setCustomizeSwiperSlidesNumber');

        const customizeSlider = document.querySelector('.swiper-customize-images-container');
        if (!customizeSlider) {
          if (this.debug) console.log('No customize slider found');
          return;
        }

        const requiredSlidesNumber = Number(this.quantity);
        if (this.debug) console.log(`Required slides number: ${requiredSlidesNumber}`);

        document
          .querySelector('modal-customization')
          ?.setAttribute('data-current-slides-num', requiredSlidesNumber.toString());

        const swiperWrapper = customizeSlider.querySelector('.swiper-wrapper');
        const slides = customizeSlider.querySelectorAll('.swiper-slide');
        const actualSlidesNumber = slides.length;
        if (this.debug) console.log(`Actual slides number: ${actualSlidesNumber}`);

        if (requiredSlidesNumber === actualSlidesNumber && requiredSlidesNumber > 1) {
          if (this.debug) console.log('Number of slides matches the requirement and is not 1');

          const actualBgImage = slides[0].querySelector('.background-image').style.backgroundImage;
          slides.forEach((slide, index) => {
            if (index !== 0) {
              const bgImage = slide.querySelector('.background-image');
              if (bgImage.style.backgroundImage !== actualBgImage) {
                bgImage.style.backgroundImage = actualBgImage; // Update with the first slide's bg image
                if (this.debug) console.log(`Updated background image for slide at index ${index}`);
              }
            }
          });
        } else if (requiredSlidesNumber > actualSlidesNumber && requiredSlidesNumber > 1) {
          if (this.debug) console.log('Required slides number is greater than actual slides number');

          const initialSlide = customizeSlider.querySelector('.swiper-slide');
          const newSlide = initialSlide.cloneNode(true);
          newSlide.setAttribute('data-index', '2');
          const previewText = newSlide.querySelector('.preview-text');
          if (previewText) {
            previewText.id = 'preview-text-2';
            if (this.debug) console.log('Updated new slide preview text ID');
          }

          swiperWrapper.appendChild(newSlide);
        } else if (requiredSlidesNumber < actualSlidesNumber && requiredSlidesNumber > 0) {
          if (this.debug) console.log('Required slides number is less than actual slides number');

          swiperWrapper.removeChild(swiperWrapper.querySelector('.swiper-slide[data-index="2"]'));
          if (this.debug) console.log('Removed slide with data-index="2"');
        }
      }
      formatCurrency(amount) {
        // Check if the amount is a whole number
        const isWholeNumber = Number.isInteger(amount);

        // Use Intl.NumberFormat with appropriate options
        return new Intl.NumberFormat(Shopify.locale, {
          style: 'currency',
          currency: Shopify.currency.active,
          minimumFractionDigits: isWholeNumber ? 0 : 2, // Set to 0 if whole number, else 2
          maximumFractionDigits: 2, // Limit to 2 decimal places
        }).format(amount);
      }

      desktopScroll() {
        if (this.debug) console.log('desktopScroll');
        // Select both containers
        const flexContainer = document.querySelector('configurable-product');
        const rightContainer = document.querySelector('.ProductSections');

        // Track the scroll direction and position
        let lastScrollY = 0;

        // Function to handle the scroll behavior
        const handleScroll = (event) => {
          // Only execute the logic if the window width is greater than 820px AND if a modal-window is not open
          if (window.innerWidth > 820 && !document.querySelector('.modal-visuallyshow')) {
            const isScrollingDown = event.deltaY > 0;

            // Check if we're at the top of the page (only scroll the right container if the page is at the top)
            if (window.scrollY === 0) {
              // If we're scrolling down on the parent container and the right container isn't at the bottom, scroll the right container
              if (
                isScrollingDown &&
                rightContainer.scrollTop < rightContainer.scrollHeight - rightContainer.clientHeight
              ) {
                rightContainer.scrollTop += event.deltaY;
                event.preventDefault(); // Prevent page scroll while syncing the right container
                document.body.style.overflow = 'hidden'; // Prevent page scroll while the right container is still scrollable
              }

              // If we're scrolling up on the parent container and the right container isn't at the top, scroll the right container
              else if (!isScrollingDown && rightContainer.scrollTop > 0) {
                rightContainer.scrollTop += event.deltaY;
                event.preventDefault(); // Prevent page scroll while syncing the right container
                document.body.style.overflow = 'hidden'; // Prevent page scroll while the right container is still scrollable
              }
            } else {
              // If the page is not at the top, allow the page to scroll normally
              document.body.style.overflow = 'auto';
            }

            // If the right container reaches the bottom, allow the page to scroll
            if (rightContainer.scrollTop >= rightContainer.scrollHeight - rightContainer.clientHeight) {
              document.body.style.overflow = 'auto'; // Allow page scrolling when right container reaches bottom
              window.scrollBy(0, event.deltaY); // Scroll the page down based on wheel scroll
            }

            // If the right container reaches the top, stop page scrolling and allow parent scroll to control it again
            if (rightContainer.scrollTop <= 0) {
              document.body.style.overflow = 'auto'; // Allow page scrolling when right container reaches top
            }

            // Track the scroll position to detect if we've reached the top or bottom of the page
            lastScrollY = window.scrollY;
          }
        };

        // Listen for scroll events on the parent container
        flexContainer.addEventListener('wheel', handleScroll);
      }

      updateSpecsModal() {
        if (this.debug) console.log('updateSpecsModal');

        const specsModal = document.querySelector('[data-product-specs-modal]');
        if (!specsModal) return;
        const activeFinish = this.getActiveFinish();
        const productId = activeFinish.dataset.configProductId;
        const variantId = activeFinish.dataset.configVariantId;

        const json_config = document.querySelector(`script[data-json-config-product-id="${productId}"]`);

        const jsonData = JSON.parse(json_config.innerHTML);
        const targetVariant = jsonData.variants.find((variant) => variant.id === variantId);
        if (!targetVariant) return;

        const productSpecs = Array.isArray(jsonData?.specs) ? jsonData.specs : [];
        const variantSpecs = Array.isArray(targetVariant?.specs) ? targetVariant.specs : [];

        const imageUrl = jsonData.dimension_image;

        // Helper function to update HTML content safely
        const updateContent = (selector, content) => {
          const element = specsModal.querySelector(selector);
          if (element && content) {
            element.innerHTML = content;
            if (this.debug) console.log(`Updated ${selector} with content:`, content);
          } else if (this.debug) {
            if (!element) console.log(`Could not find element with selector: ${selector}`);
            if (!content) console.log(`Content for ${selector} is empty`);
            console.warn(`Element with selector ${selector} not found`);
          }
        };

        // Helper function to update <picture>
        const updatePictureSource = (pictureSelector, newSrc) => {
          if (!pictureSelector || !newSrc) {
            if (this.debug) console.log(`Invalid parameters: pictureSelector: ${pictureSelector}, newSrc: ${newSrc}`);
            return;
          }

          const pictureElement = specsModal.querySelector(pictureSelector);
          if (!pictureElement) return;

          // Update each <source> element within <picture>
          const sourceElements = pictureElement.querySelectorAll('source');
          sourceElements.forEach((source) => {
            source.setAttribute('data-srcset', newSrc);
            source.setAttribute('srcset', newSrc);
          });

          // Update the <img> element within <picture>
          const imgElement = pictureElement.querySelector('img');
          if (imgElement) {
            imgElement.setAttribute('data-src', newSrc);
            imgElement.setAttribute('src', newSrc);
          }

          if (this.debug) console.log(`Updated picture sources with new src: ${newSrc}`);
        };

        // Update modal with variant-specific data
        // updateContent('[spec-variant-material]', this.findSpecValue(variantSpecs, '[spec-variant-material]'));
        updateContent('[spec-variant-warranty]', this.findSpecValue(variantSpecs, '[spec-variant-warranty]'));
        updateContent('[spec-variant-sku]', targetVariant.sku || '');
        updateContent('[spec-variant-coating]', targetVariant.finish || '');

        // Update modal with product-level data
        updateContent('[spec-product-capacity]', this.findSpecValue(productSpecs, '[spec-product-capacity]'));
        updateContent('[spec-product-linercode]', this.findSpecValue(productSpecs, '[spec-product-linercode]'));
        updateContent('[spec-product-power-source]', this.findSpecValue(productSpecs, '[spec-product-power-source]'));
        updateContent('[spec-product-ingredients]', this.findSpecValue(productSpecs, '[spec-product-ingredients]'));
        updateContent('[spec-product-magnification]', this.findSpecValue(productSpecs, '[spec-product-magnification]'));
        updateContent('[spec-product-compatibility]', this.findSpecValue(productSpecs, '[spec-product-compatibility]'));
        updateContent('[spec-product-battery-life]', this.findSpecValue(productSpecs, '[spec-product-battery-life]'));

        // Update dimension image
        updatePictureSource('[data-product-dimension-image]', imageUrl);

        if (this.debug) console.log('Updated modal with variant:', targetVariant, 'and product specs:', jsonData.specs);
      }

      showSpecsModalOpener() {
        const opener = document.querySelector('[data-modal="#ProductSpecsModal"]');
        if (!opener) return;

        const config_finish = this.querySelector('config-finish');

        if (config_finish?.querySelector('[data-button-state="active"]')) {
          opener.classList.remove('tw-hidden');
        } else {
          opener.classList.add('tw-hidden');
        }
      }

      updateProductFeatures() {
        const storedHandle = sessionStorage.getItem('handle');

        if (this.handle && storedHandle !== this.handle) {
          sessionStorage.setItem('handle', this.handle);

          fetch(`/products/${this.handle}?section_id=product-features`)
            .then((response) => response.text())
            .then((data) => {
              const html = new DOMParser().parseFromString(data, 'text/html');
              const newProductFeatures = html.querySelector('.js-product-features-section');

              if (newProductFeatures) {
                const oldProductFeatures = document.querySelector('.js-product-features-section');

                if (oldProductFeatures) {
                  oldProductFeatures.replaceWith(newProductFeatures);

                  if (window.lazyLoadInstance && typeof window.lazyLoadInstance.update === 'function') {
                    window.lazyLoadInstance.update(document.querySelectorAll('.js-product-features-section .lazy'));
                  }
                  // reinit swiper and reattach listeners
                  if (
                    window.reinitProductFeaturesScripts &&
                    typeof window.reinitProductFeaturesScripts === 'function'
                  ) {
                    window.reinitProductFeaturesScripts();
                  }
                }
              }
            })
            .catch((error) => console.error('Error:', error));
        } else {
          if (this.debug) console.log('handle not changed, not need to fetch');
        }
      }

      setProgressBar() {
        // Check if the viewport matches the CSS breakpoint (min-width: 820px)
        if (!window.matchMedia('(min-width: 820px)').matches) {
          return;
        }
        if (this.debug) console.log('setProgressBar');
        const activeSections = Array.from(document.querySelectorAll('[data-section-state]:not(.tw-hidden)')).filter(
          (section) => !section.matches('config-cart-summary') && !section.matches('modal-opener')
        );

        const progressBar = document.querySelector('[data-progress-bar]');
        const progressBarStepsWrap = progressBar?.querySelector('[data-progress-bar-steps]');
        const progressBarIndicator = progressBar?.querySelector('[data-progress-bar-indicator]');

        if (activeSections.length > 2 && progressBar && progressBarStepsWrap && progressBarIndicator) {
          // See related styles in "section-configurable-product.css"
          // and TW classes in "configurable-product.liquid"
          // and js variables initialIndicatorHeight and stepHeight in "configurable-product.js"
          // If need to make changes, also change ones in the above-mentioned files.

          let initialIndicatorHeight = 4; // This is ".config-progress-bar-indicator height", which is equal to ".config-progress-bar-step height"
          let stepHeight = 18; // This is sum of "div .config-progress-bar-steps tw-gap" and ".config-progress-bar-step height"

          // Disconnect any existing observers
          if (this.progressObserver) {
            this.progressObserver.disconnect();
          }

          progressBarStepsWrap.classList.add('hidden');
          progressBarIndicator.classList.add('hidden');
          progressBarStepsWrap.innerHTML = '';

          // Function to calculate and update progress bar height
          const updateProgressBarHeight = () => {
            let multiplier = -1;
            activeSections.forEach((section) => {
              const activeButton = section.querySelector('[data-button-state="active"]');
              if (activeButton) multiplier += 1;
            });
            progressBarIndicator.style.height = `${initialIndicatorHeight + stepHeight * Math.max(0, multiplier)}px`;
          };

          activeSections.forEach((section) => {
            const step = document.createElement('span');
            step.classList.add('config-progress-bar-step');
            progressBarStepsWrap.appendChild(step);
          });

          // calculate and update progress bar height initially
          updateProgressBarHeight();

          // Create a MutationObserver
          this.progressObserver = new MutationObserver(() => {
            updateProgressBarHeight();
          });

          // Observe each section's buttons for `data-button-state` changes
          activeSections.forEach((section) => {
            const buttons = section.querySelectorAll('[data-button-state]');
            buttons.forEach((button) => {
              this.progressObserver.observe(button, { attributes: true });
            });
          });

          progressBarStepsWrap.classList.remove('hidden');
          progressBarIndicator.classList.remove('hidden');
          progressBar.classList.remove('hidden');
        }
      }

      /**
       * Handles sticky behavior of the mobile swiper gallery when scrolling on mobile devices.
       *
       * - Uses GSAP's ScrollTrigger to detect the scroll position and toggles the location and state
       *   of a product image swiper based on the scroll position and viewport width.
       * - On mobile view, repositions the swiper gallery close to the product pickers section when leaving the viewport
       *   and restores it to its original position when returning to the viewport.
       * - Listens for window resize events and dynamically destroys or recreates ScrollTrigger instances
       *   to ensure proper behavior when switching between mobile and desktop views.
       *
       * Preconditions:
       * - Requires GSAP's ScrollTrigger plugin to be available globally.
       *
       * DOM Element Prerequisites:
       * - `.product-carousel-container`: The swiper gallery container.
       * - `config-finish`: The pickers section elements.
       * - `.ProductHeader-wrapper--mobile`: The mobile product title wrapper.
       * - `.section-header`: Header section to calculate offsets.
       *
       * Custom CSS Variable:
       * - `--moved-swiper-top` is updated dynamically to reflect position adjustments.
       */
      handleStickyMobileSwiper() {
        if (this.debug) console.log('handleStickyMobileSwiper');

        const swiperGallery = document.querySelector('.product-carousel-container');
        const pickersSection = document.querySelector('config-model') && document.querySelector('config-finish');
        const mobileProductTitle = document.querySelector('.ProductHeader-wrapper--mobile');
        const gsapScrollTrigger = window.ScrollTrigger;
        const MOVED_SWIPER_TOP_POSITION_CSS_PROPERTY = '--moved-swiper-top';

        if (!swiperGallery || !pickersSection || !gsapScrollTrigger || !mobileProductTitle) {
          if (this.debug)
            console.warn('Warn: swiperGallery, pickersSection, mobileProductTitle or gsapScrollTrigger not found.');
          return;
        }

        let scrollerStart = 0;
        let scrollerEnd = 0;
        let scrollTriggerInstance = null;
        let isMobileView = window.matchMedia('(min-width: 820px)').matches === false;
        let windowWidth = window.innerWidth;

        /**
         * Updates the start and end limits for the ScrollTrigger scroller.
         * - `scrollerStart` is set to the offset height of the header or 0 if the header is not found.
         * - `scrollerEnd` is calculated as the sum of the swiper gallery's offset height and `scrollerStart`.
         */
        const getScrollerLimits = () => {
          const header = document.querySelector('.section-header');

          scrollerStart = header?.offsetHeight || 0;
          scrollerEnd = swiperGallery.offsetHeight + scrollerStart;

          if (this.debug) console.log('Updated limits:', { scrollerStart, scrollerEnd });
        };

        /**
         * Moves the swiper gallery to a position right above the pickers section
         * by updating the top position CSS variable and reordering DOM elements.
         * Intended for mobile view when the user scrolls past the gallery.
         *
         * - Updates the `--moved-swiper-top` CSS variable to align with the top position.
         * - Moves the swiperGallery DOM element to be before the pickersSection.
         * - Adds the 'moved' class to the swiperGallery.
         */
        const moveSwiperToPickers = () => {
          document.documentElement.style.setProperty(
            MOVED_SWIPER_TOP_POSITION_CSS_PROPERTY,
            `${pickersSection.offsetTop - swiperGallery.offsetHeight}px`
          );
          pickersSection.before(swiperGallery);
          swiperGallery.classList.add('moved');
        };

        /**
         * Moves the swiper gallery back to its initial position below the mobile product title.
         * - Removes the 'moved' class from the swiperGallery element.
         * - Inserts the swiperGallery DOM element back after the mobileProductTitle.
         */
        const moveSwiperToInitialPosition = () => {
          swiperGallery.classList.remove('moved');
          mobileProductTitle.after(swiperGallery);
        };

        /**
         * Creates a ScrollTrigger instance for handling scroll-based swiper gallery behavior on mobile devices.
         *
         * - Fetches and calculates the scroller limits using `getScrollerLimits`.
         * - Initializes ScrollTrigger with specified start and end trigger points.
         * - Adds sticky behavior to the swiperGallery based on scroll position.
         * - Calls `moveSwiperToPickers` when the gallery leaves its original position.
         * - Calls `moveSwiperToInitialPosition` when the gallery scrolls back into view.
         */
        const createScrollTrigger = () => {
          if (!isMobileView) return;

          getScrollerLimits();

          scrollTriggerInstance = gsapScrollTrigger.create({
            markers: false,
            trigger: swiperGallery,
            start: `top ${scrollerStart}px`,
            endTrigger: pickersSection,
            end: `top ${scrollerEnd}px`,
            toggleClass: 'sticky',
            onLeave: () => moveSwiperToPickers(),
            onEnterBack: () => moveSwiperToInitialPosition(),
          });

          if (this.debug) console.log('ScrollTrigger created.');
        };

        /**
         * Destroys the currently active ScrollTrigger instance.
         *
         * - Kills the ScrollTrigger instance if it exists.
         * - Resets the `scrollTriggerInstance` to null.
         */
        const destroyScrollTrigger = () => {
          if (scrollTriggerInstance) {
            scrollTriggerInstance.kill();
            scrollTriggerInstance = null;

            if (this.debug) console.log('ScrollTrigger destroyed.');
          }
        };

        /**
         * Handles resizing of the viewport and adjusts the behavior of the Swiper gallery
         * based on whether the viewport switches between mobile and desktop views.
         *
         * - Checks if the view mode has changed (mobile to desktop or vice versa).
         * - Destroys and recreates the ScrollTrigger instance if the view mode changes.
         * - Resets the page scroll position and gallery behavior when switching to mobile view.
         * - Moves the swiper gallery back to its initial position and removes the sticky class when switching to desktop view.
         */
        const handleResize = () => {
          if (this.debug) console.log('handleResize');
          const actualMobileView = window.matchMedia('(min-width: 820px)').matches === false;

          if (actualMobileView !== isMobileView) {
            isMobileView = actualMobileView;
            if (isMobileView) {
              window.scrollTo(0, 0);
              createScrollTrigger();

              if (this.debug) console.log('Switching to mobile view.');
            } else {
              destroyScrollTrigger();
              swiperGallery.classList.remove('sticky');
              moveSwiperToInitialPosition();

              if (this.debug) console.log('Switching to desktop view.');
            }
          }
        };

        if (isMobileView) createScrollTrigger();

        window.addEventListener('resize', () => {
          if (window.innerWidth !== windowWidth) {
            windowWidth = window.innerWidth;
            handleResize();
          }
        });
      }

      /**
       * Updates delivery time and message elements based on the provided product variant.
       *
       * - Updates the variant ID in the BIS (Back-In-Stock) button and form.
       * - Modifies visibility of delivery message, delivery accent, and BIS button
       *   based on the variant's properties.
       * - Handles special conditions for variants with SKUs starting with "GC" and
       *   those having a `delivery_message`.
       *
       * @param {Object} variant - The variant object containing product details.
       * @param {string} [variant.id] - The unique identifier of the product variant.
       * @param {string} [variant.sku] - The SKU (Stock Keeping Unit) of the product variant.
       * @param {string} [variant.delivery_message] - The delivery message for the product variant.
       * @param {string} [variant.available] - The availability status of the variant ("true" or "false").
       */
      updateDeliveryTime(variant) {
        const bisButton = document.querySelector('config-cart-summary [data-notify-btn]');
        const deliveryMessageEl = document.querySelector('config-cart-summary [data-pdp-delivery-message]');
        const deliveryAccentEl = document.querySelector('config-cart-summary [data-pdp-delivery-accent]');
        const deliveryMessagePropsInput = document.getElementById('properties-delivery_message');
        const bisVariantId = document.getElementById('bis_variant_id');

        if (!variant) {
          if (deliveryMessageEl.innerHTML === '') {
            deliveryAccentEl.classList.add('hidden');
            this.shipsIn(deliveryMessageEl);
            deliveryMessagePropsInput.value = '';
            deliveryMessageEl.classList.remove('hidden');
          }
        } else {
          // update bis_variant_id of #bis_modal form
          if (bisVariantId) bisVariantId.value = variant.id;
          bisButton.dataset.variantId = variant.id;

          //if the variant.sku starts with "GC", then we show the delivery message
          if (variant.sku && variant.sku.startsWith('GC')) {
            bisButton.classList.add('hidden');
            if (deliveryAccentEl) deliveryAccentEl.classList.remove('hidden');
          } else if (variant.available === 'false') {
            deliveryMessageEl.textContent = '';
            deliveryAccentEl.classList.add('hidden');
            bisButton.classList.remove('hidden');
          } else if (variant.delivery_message) {
            bisButton.classList.add('hidden');
            deliveryMessageEl.textContent = decodeURI(variant.delivery_message);
            deliveryMessagePropsInput.value = decodeURI(variant.delivery_message);
            deliveryAccentEl.classList.remove('hidden');
          } else {
            bisButton.classList.add('hidden');
            this.shipsIn(deliveryMessageEl);
            deliveryMessagePropsInput.value = '';
            deliveryAccentEl.classList.remove('hidden');
          }
        }
      }

      /**
       * Updates the delivery message element with an appropriate shipping message.
       * Determines the message based on the current time, day, country code, and other conditions.
       *
       * @param {HTMLElement} [deliveryMessageEl=document.querySelector('config-cart-summary [data-pdp-delivery-message]')]
       *   The delivery message element to update. Defaults to selecting the element with the specified selector.
       */
      shipsIn(deliveryMessageEl = document.querySelector('config-cart-summary [data-pdp-delivery-message]')) {
        if (!deliveryMessageEl) return;

        if (deliveryMessageEl.classList.contains('override')) {
          deliveryMessageEl.classList.remove('override');
          return;
        }

        if (window.theme.shipping_message_override) {
          deliveryMessageEl.innerHTML = window.theme.pdp_delivery_message;
        } else {
          const country_code = window.theme.country_code;
          const day = new Date().getDay();
          // Sunday - Saturday : 0 - 6
          const hours = new Date().getHours();
          const hour_cutoff = parseInt(window.theme.pdp_delivery_time_cutoff);

          const customizationSection = document.querySelector('config-customize');

          if (country_code === 'GB') {
            if (day >= 1 && day <= 4) {
              if (hours < hour_cutoff) {
                deliveryMessageEl.innerHTML = window.theme.pdp_delivery_message_today;
              } else {
                deliveryMessageEl.innerHTML = window.theme.pdp_delivery_message_tomorrow;
              }
            } else if (day === 5) {
              if (hours < hour_cutoff) {
                deliveryMessageEl.innerHTML = window.theme.pdp_delivery_message_monday;
              } else {
                deliveryMessageEl.innerHTML = window.theme.pdp_delivery_message_tuesday;
              }
            } else {
              deliveryMessageEl.innerHTML = window.theme.pdp_delivery_message_tuesday;
            }
          } else {
            if (
              customizationSection &&
              customizationSection.querySelector('[data-config-customize="true"][data-button-state="active"]')
            ) {
              deliveryMessageEl.innerHTML =
                window.theme.pdp_delivery_message_ships + ' ' + window.theme.pdp_delivery_message_personalization;
            } else if (day === 0) {
              deliveryMessageEl.innerHTML =
                window.theme.pdp_delivery_message_ships + ' ' + window.theme.pdp_delivery_message_tomorrow;
            } else if ((day === 5 && hours >= hour_cutoff) || day === 6) {
              deliveryMessageEl.innerHTML =
                window.theme.pdp_delivery_message_ships + ' ' + window.theme.pdp_delivery_message_monday;
            } else if (hours >= hour_cutoff) {
              deliveryMessageEl.innerHTML =
                window.theme.pdp_delivery_message_ships + ' ' + window.theme.pdp_delivery_message_tomorrow;
            } else {
              deliveryMessageEl.innerHTML =
                window.theme.pdp_delivery_message_ships + ' ' + window.theme.pdp_delivery_message_today;
            }
          }
        }
      }

      // Handles lazy-loading images using requestIdleCallback or a fallback.
      loadLazyImagesWithFallback() {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            if (window.lazyLoadInstance && typeof window.lazyLoadInstance.loadAll === 'function') {
              window.lazyLoadInstance.loadAll();
              if (this.debug) console.log('All lazy-loaded images have been loaded (requestIdleCallback)');
            }
          });
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            if (window.lazyLoadInstance && typeof window.lazyLoadInstance.loadAll === 'function') {
              window.lazyLoadInstance.loadAll();
              if (this.debug) console.log('All lazy-loaded images have been loaded (setTimeout fallback)');
            }
          }, 200); // Small delay to avoid blocking critical tasks
        }
      }
    }
  );
}
